/**
 * Numeric sort key for org rank ordering (higher = more senior).
 * Prefer `rankSortKey` from API (parentId tree via sortOrgRanksForDisplay).
 * Fallback: rankType strata + payGrade parse when rankSortKey is absent.
 */

const RANK_TYPE_ORDER = {
  'Commissioned Officer': 4,
  'Warrant Officer': 3,
  Enlisted: 2,
  Affiliated: 1
};

function parsePayGradeParts(payGrade) {
  const s = String(payGrade ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
  if (!s) return null;
  const oew = s.match(/^([OEW])(?:-)?(\d+)$/);
  if (oew) {
    return { letter: oew[1], n: parseInt(oew[2], 10) };
  }
  const a = s.match(/^A(?:-)?(\d+)$/);
  if (a) {
    return { letter: 'A', n: parseInt(a[1], 10) };
  }
  return null;
}

function inferRankTypeFromParts(parts) {
  if (!parts) return null;
  if (parts.letter === 'O') return 'Commissioned Officer';
  if (parts.letter === 'W') return 'Warrant Officer';
  if (parts.letter === 'E') return 'Enlisted';
  if (parts.letter === 'A') return 'Affiliated';
  return null;
}

function getPayGradeFallbackSortKey(row) {
  const payGrade = row?.payGrade;
  const parts = parsePayGradeParts(payGrade);
  const rankType = row?.rankType || inferRankTypeFromParts(parts);

  if (!parts || !rankType || RANK_TYPE_ORDER[rankType] == null) {
    return Number.POSITIVE_INFINITY;
  }

  const tier = RANK_TYPE_ORDER[rankType];

  if (rankType === 'Commissioned Officer' && parts.letter === 'O') {
    return tier * 1_000_000 + parts.n;
  }

  if (rankType === 'Warrant Officer' && parts.letter === 'W') {
    return tier * 1_000_000 + (50 - parts.n);
  }

  if (rankType === 'Enlisted' && parts.letter === 'E') {
    return tier * 1_000_000 + parts.n;
  }

  if (rankType === 'Affiliated' && parts.letter === 'A') {
    return tier * 1_000_000 + parts.n;
  }

  const inferred = inferRankTypeFromParts(parts);
  if (inferred && inferred !== rankType) {
    return getPayGradeFallbackSortKey({ payGrade, rankType: inferred });
  }

  return Number.POSITIVE_INFINITY;
}

/**
 * @param {{
 *   rankSortKey?: number|null,
 *   payGrade?: string|null,
 *   rankType?: string|null
 * }} row
 * @returns {number} sort key; larger = higher rank.
 */
export function getRankHierarchySortKey(row) {
  if (row?.rankSortKey != null && Number.isFinite(Number(row.rankSortKey))) {
    return Number(row.rankSortKey);
  }
  return getPayGradeFallbackSortKey(row);
}
